import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { BaseCellEditorComponent } from '../base_cell_editor.component';
import { DateTextBoxComponent } from '../../../core/date-text-box/date-text-box.component';
import { BaseDateTimePropertyViewModel } from '../../../../../view-models/base-type/date-time-property-view-model';
import { ColumnCellEditorParams } from '../column_cell_editor_params.interface';

const KEY_BACKSPACE = 8;
const KEY_DELETE = 46;
const KEY_F2 = 113;
const KEY_ENTER = 13;
const KEY_TAB = 9;
const KEY_UP = 38;
const KEY_DOWN = 40;
const KEY_LEFT = 37;
const KEY_RIGHT = 39;

// create your cellEditor as a Angular component
@Component({
    selector: 'nts-date-text-box-editor-cell',
    styleUrls: ['./date-cell-editor.component.scss'],
    standalone: true,
    imports: [
        DateTextBoxComponent
    ],
    template: `
        <nts-date-text-box 
            #textBox
            (keyDown)="onKeyDown($event)"
            (onFinishEditing)="onFinishEditing()"
            (valueChange)="valueChange($event)"
            [style.width.px]="cellWidth"
            [style.height.px]="cellHeight"
            [propertyViewModel]="datePropertyViewModel"
            [initialChar]="char"
            [initialValue]="value"
            [listenClickOutside]="true"
            [showErrorBorder]="false"
            [modelUpdate]="false">
        </nts-date-text-box>
    `
})
export class DateCellEditorComponent extends BaseCellEditorComponent<Date> implements AfterViewInit {
    
    @ViewChild('textBox', { static: true })
    set content(content: DateTextBoxComponent) {
        this.textBox = content;
    }

    override textBox: DateTextBoxComponent;
    char: string;
    highlightAllOnFocus = true;
   
    get datePropertyViewModel() {
        return this.propertyViewModel as BaseDateTimePropertyViewModel
    }

    get cellWidth() {
        return this.params.eGridCell.clientWidth;
    }

    get cellHeight() {
        return this.params.eGridCell.clientHeight;
    }

    private cancelBeforeStart = false;

    override ngAfterViewInit() {
        setTimeout(() => {
            if (this.textBox) {
                this.textBox.focus();
                this.textBox.input.select();
            }
        });
    }

    override agInit(params: ColumnCellEditorParams): void {
        this.params = params;
        this.setInitialState(this.params);

        // only start edit if key pressed is a number, not a letter
        this.cancelBeforeStart =
            params.charPress && '1234567890'.indexOf(params.charPress) < 0;
    }

    override isCancelBeforeStart(): boolean {
        return this.cancelBeforeStart;
    }
    
    override onKeyDown(event: any): void {
        if (this.isLeftOrRight(event) || this.deleteOrBackspace(event)) {
            event.stopPropagation();
            return;
        }

        if (
            !this.finishedEditingPressed(event) &&
            !this.isKeyPressedNumeric(event)
        ) {
            if (event.preventDefault) { event.preventDefault(); }
        }

        if (this.isUpOrDown(event)) {
            this.navigateToNextCell(event);
        }
    }

    isPopup(): boolean {
        return false;
    }

    onFinishEditing() {
        this.params.stopEditing();
    }

    setInitialState(params: any) {
        let startValue: Date|string = null;
        let charValue = null;
        let highlightAllOnFocus = true;

        if (params.keyPress === KEY_BACKSPACE || params.keyPress === KEY_DELETE) {
            // if backspace or delete pressed, we clear the cell
            startValue = '';
        } else if (params.charPress) {
            // if a letter was pressed, we start with the letter
            charValue = params.charPress;
            highlightAllOnFocus = true;
            startValue = charValue;
        } else {
            // otherwise we start with the current value
            startValue = params.columnInfo.fieldName.split('.').reduce(
                (o, i) => {
                    if (i === 'value') {
                        return o[i];
                    }
                    return o[i];
                }, this.params.data) as Date;
            if (params.keyPress === KEY_F2) {
                highlightAllOnFocus = true;
            }
        }

        this.char = charValue;
        this.value = startValue as Date;
        this.highlightAllOnFocus = highlightAllOnFocus;
    }

    private getCharCodeFromEvent(event: any): any {
        event = event || window.event;
        return typeof event.which == 'undefined' ? event.keyCode : event.which;
    }

    private deleteOrBackspace(event: any) {
        return (
            [KEY_DELETE, KEY_BACKSPACE].indexOf(this.getCharCodeFromEvent(event)) > -1
        );
    }

    private isLeftOrRight(event: any) {
        return [KEY_LEFT, KEY_RIGHT].indexOf(this.getCharCodeFromEvent(event)) > -1;
    }

    private isUpOrDown(event: any) {
        return [KEY_UP, KEY_DOWN].indexOf(this.getCharCodeFromEvent(event)) > -1;
    }

    private finishedEditingPressed(event: any) {
        const charCode = this.getCharCodeFromEvent(event);
        return charCode === KEY_ENTER || charCode === KEY_TAB;
    }

    private isKeyPressedNumeric(event: any): boolean {
        const charCode = this.getCharCodeFromEvent(event);
        const charStr = event.key ? event.key : String.fromCharCode(charCode);
        return this.isCharNumeric(charStr) || charStr === '/';
    }

    private isCharNumeric(charStr: string): boolean {
        return !!/\d/.test(charStr);
    }
}
